'use client';

import {
  Box,
  BrandLogo,
  CartBadge,
  Flex,
  Grid,
  GridColumn,
  Link,
  omitText,
  Typography,
} from '@kohler-dsm-library/kohler-core';
import { ArrowDownIcon, MenuIcon, SearchIcon, FavoriteNavIcon, FavoriteNavFilledIcon } from '@kohler-dsm-library/kohler-icons';
import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { NavigationItem } from './NavigationItem';
import { OverlayMenu } from '../OverlayMenu';
import useResponsiveNav from './index.hooks';
import classes from './index.module.scss';
import { mainNavigationBarVariantClasses } from './index.utils';

export const MainNavigationBar = React.forwardRef((props, ref) => {
  const {
    brandCTA,
    menuItems,
    showShadow,
    showDesktopActions = true,
    searchCTA,
    menuCTA,
    searchText = 'Search',
    buttonCTA,
    designerTradeCTA, 
    variant = 'universal',
    utilityLinks,
    showBackground = false,
    mod,
    openMobileMenu,
    closeCTA,
    setIsOpenMobileMenu,
    isStatic,
    style,
    showMenuDelay,
    transitionDuration,
    isAlwaysSticky,
    ...rest
  } = props;

  const isUniversal = variant === 'universal';
  const { brand: brandType, ...brandCTAProps } = brandCTA;
  const [activeMenu, setActiveMenu] = useState(null);
  const hoverTimeoutRef = useRef(null);

  const { visibleItems, overflowItems, isMoreOpen, setIsMoreOpen, navRef, containerRef, moreButtonRef } =
    useResponsiveNav(menuItems);

  //handle hover enter/leave for menu item and dropdown
  const handleMenuMouseEnter = (navText) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setActiveMenu(navText);
  };
  const handleMenuMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 100); // 100ms delay for hover gap fix
  };

  const isMobile = typeof window !== 'undefined'
    ? window.innerWidth < 1024
    : false;

  const isTablet = typeof window !== 'undefined'
    ? window.innerWidth >= 768 && window.innerWidth < 1024
    : false;

  return (
    <Box
      className={clsx(
        classes.mainNav,
        showShadow && classes.mainNavShadow,
        mainNavigationBarVariantClasses[variant],
        showBackground && classes.mainNavBackground,
        (isStatic || showBackground) && classes.mainNavRelative,
        'kohler-main-navigation-bar-root'
      )}
      ref={ref}
      style={{
        '--transition-overlay-duration': typeof transitionDuration === 'number' ? `${transitionDuration}ms` : undefined,
        ...style,
      }}
      mod={mod}
      {...rest}
    >
      <Grid>
        <GridColumn>
          {/* Mobile: Hamburger + Logo (left), Spacer, Utilities (right) */}
          {isMobile ? (
            <Flex className={classes.mainNavMobileRow}>
              {/* Left group: Hamburger + Logo */}
              <Flex className={classes.leftGroup}>
                <Box
                  component="button"
                  type="button"
                  aria-label="open menu"
                  {...menuCTA}
                  className={clsx(isUniversal ? classes.menuIconUniversal : classes.menuIconVTwo, menuCTA?.className)}
                  onClick={(e) => {
                    if (setIsOpenMobileMenu) setIsOpenMobileMenu(true);
                    if (menuCTA?.onClick) menuCTA.onClick(e);
                  }}
                >
                  <MenuIcon size="medium" withBoundingBox={false} />
                </Box>
                <Box component="a" className={classes.brandLogo} aria-label={`${brandType} logo`} {...brandCTAProps}>
                  <BrandLogo brand={brandType} />
                </Box>
              </Flex>
              {/* Spacer */}
              <div style={{ flex: 1 }} />
              {/* Utilities (right) */}
              <Flex className={classes.utilitiesMobile} gap={15}>
                <Box
                  component="button"
                  type="button"
                  aria-label={searchText}
                  {...searchCTA}
                  className={clsx(classes.utilitiesSearchMobile, searchCTA?.className)}
                >
                  <SearchIcon className={classes.utilitiesSearchIcon} withBoundingBox={false} />
                </Box>

              {utilityLinks?.favoritesCTA && (
                <Box
                  component="a"
                  display="flex"
                  aria-label="see favorites"
                  textColor="var(--content-primary)"
                  {...utilityLinks.favoritesCTA}
                >
                  {utilityLinks.favoritesCTA.isFilled ? (
                    <FavoriteNavFilledIcon withBoundingBox={false} />
                  ) : (
                    <FavoriteNavIcon withBoundingBox={false} />
                  )}
                </Box>
              )}
                {utilityLinks?.cartBadgeCTA && (
                  <CartBadge
                    component="a"
                    aria-label="see shopping cart"
                    {...utilityLinks.cartBadgeCTA}
                    className={clsx(classes.utilityLink, utilityLinks.className)}
                  />
                )}
              </Flex>
            </Flex>
          ) : (
            <Flex className={isUniversal ? classes.mainNavWrapperUniversal : classes.mainNavWrapperDefault}>
              <Box
                component="button"
                type="button"
                aria-label="open menu"
                {...menuCTA}
                className={clsx(isUniversal ? classes.menuIconUniversal : classes.menuIconVTwo, menuCTA?.className)}
                onClick={(e) => {
                  if (setIsOpenMobileMenu) setIsOpenMobileMenu(true);
                  if (menuCTA?.onClick) menuCTA.onClick(e);
                }}
              >
                <MenuIcon size="medium" withBoundingBox={false} />
              </Box>
              {/* Menu links (logo inside) */}
            <Flex className={clsx(isUniversal ? classes.menuItemsUniversal : classes.menuItemsVTwo, classes.menuItems)}>
              <Box component="a" className={classes.brandLogo} aria-label={`${brandType} logo`} {...brandCTAProps}>
                <BrandLogo brand={brandType} />
              </Box>

              {menuItems && (
                <Box
                  ref={containerRef}
                  className={classes.menuLinksWrapper}
                  component="nav"
                  aria-label="Main navigation"
                >
                  <Flex
                    className={clsx(isUniversal ? classes.menuLinksUniversal : classes.menuLinksVTwo, classes.menuLinks)}
                    component="ul"
                    ref={navRef}
                  >
                    {visibleItems.map((navItem, idx) => (
                      <React.Fragment key={navItem.text}>
                      <Box
                        component="li"
                        key={navItem.text}
                        id={navItem.text || `nav-item-${navItem.text}`}
                        className={clsx(classes.navigationItemWrapper)}
                          onMouseEnter={() => handleMenuMouseEnter(navItem.text)}
                          onMouseLeave={handleMenuMouseLeave}
                        onKeyDown={event => {
                          if (event.key === 'Escape') {
                            setActiveMenu(null);
                            event.currentTarget.querySelector('button')?.focus();
                          }
                        }}
                      >
                        <NavigationItem
                          text={navItem.text}
                          isSelected={activeMenu === navItem.text}
                          href={navItem.CTALists ? undefined : navItem.href}
                          component={navItem.CTALists ? 'button' : undefined}
                          type={navItem.CTALists ? 'button' : undefined}
                          aria-expanded={navItem.CTALists ? activeMenu === navItem.text : undefined}
                          onClick={navItem.CTALists
                            ? () => setActiveMenu(previous => previous === navItem.text ? null : navItem.text)
                            : navItem.onClick}
                          variant={variant}
                          className={clsx(
                            isUniversal ? classes.navItemUniversal : classes.navItemVTwo,
                            classes.navItem,
                            activeMenu === navItem.text && isUniversal && classes.navItemUnderline,
                            activeMenu === navItem.text && !isUniversal && classes.navItemUnderlineV2
                          )}
                        />
                        {navItem.CTALists && activeMenu === navItem.text && (
                          <Box
                            className={clsx(
                              classes.navigationOverlayMenuWrapper,
                              classes.navigationOverlayMenuVisible
                            )}
                            onMouseEnter={() => handleMenuMouseEnter(navItem.text)}
                            onMouseLeave={handleMenuMouseLeave}
                          >
                            <OverlayMenu mod="white" {...navItem} />
                          </Box>
                        )}
                      </Box>
                        {idx === 2 && (
                          <li aria-hidden="true" className={classes.menuItemDivider} />
                        )}
                      </React.Fragment>
                    ))}

                    {overflowItems.length > 0 && (
                      <Box
                        component="li"
                        ref={moreButtonRef}
                        className={clsx(classes.navigationItemWrapper, classes.moreButton)}
                        onMouseEnter={() => setIsMoreOpen(true)}
                        onMouseLeave={() => setIsMoreOpen(false)}
                        onKeyDown={event => {
                          if (event.key === 'Escape') {
                            setIsMoreOpen(false);
                            event.currentTarget.querySelector('button')?.focus();
                          }
                        }}
                      >
                        <NavigationItem
                          component="button"
                          type="button"
                          aria-expanded={isMoreOpen}
                          onClick={() => setIsMoreOpen(open => !open)}
                          className={clsx(isUniversal ? classes.navItemUniversal : classes.navItemVTwo, classes.navItem)}
                          text={
                            <Flex gap={5} align="center">
                              More <ArrowDownIcon size="xSmall" withBoundingBox={false} style={{ flexShrink: 0 }} />
                            </Flex>
                          }
                          variant={variant}
                        />
                        {isMoreOpen && (
                          <Box
                            className={clsx(
                              classes.navigationOverlayMenuWrapper,
                              classes.navigationOverlayMenuVisible
                            )}
                          >
                            <OverlayMenu mod="white" isMoreOverlay mainMenuItems={overflowItems} />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Flex>
                </Box>
              )}
            </Flex>

            {(showDesktopActions || buttonCTA) && (
              <Flex className={clsx(classes.utilities, isUniversal ? classes.utilitiesUniversalGap : classes.utilitiesDefaultGap)}>
                  <Flex className={classes.appointmentSearchIcons}>
                    <Flex className={classes.desktopIconsButtons}>
                      {!isTablet && 
                        <Flex className={classes.orderRequest}>
                          {designerTradeCTA && (
                            <Typography
                              className={classes.designerTradeText}
                              component="a"
                              href={designerTradeCTA.href}
                              target={designerTradeCTA.target}
                            >
                              {designerTradeCTA.text}
                            </Typography>
                          )}
                          {!isMobile && buttonCTA && (
                            <Link
                              className={classes.requestAppointmentCTA}
                              href={buttonCTA.href}
                              target={buttonCTA.target}
                              {...omitText(buttonCTA)}
                            >
                              {buttonCTA.text}
                            </Link>
                          )}
                        </Flex>
                      }
                    <Box
                      component="button"
                      id="desktopSearchBtn"
                      type="button"
                      aria-label={searchText}
                      {...searchCTA}
                      className={clsx(classes.utilitiesSearch, searchCTA?.className)}
                    >
                      <Typography size="paragraph-small" component="span">
                        {searchText}
                      </Typography>
                      <SearchIcon className={classes.utilitiesSearchIcon} withBoundingBox={false} />
                    </Box>
                  </Flex>
                </Flex>


                <Flex className={classes.utilitiesMobile} gap={15}>
                  <Box
                    component="button"
                    type="button"
                    aria-label={searchText}
                    {...searchCTA}
                    className={clsx(classes.utilitiesSearchMobile, searchCTA?.className)}
                  >
                    <SearchIcon className={classes.utilitiesSearchIcon} withBoundingBox={false} />
                  </Box>

                  {utilityLinks?.cartBadgeCTA && (
                    <CartBadge
                      component="a"
                      aria-label="see shopping cart"
                      {...utilityLinks.cartBadgeCTA}
                      className={clsx(classes.utilityLink, utilityLinks.className)}
                    />
                  )}

                  </Flex>
                </Flex>
              )}
              </Flex>
            )}

            {isMobile && openMobileMenu && (
              <OverlayMenu
                mainMenuItems={menuItems}
                className={classes.mobileOverlayMenu}
                setIsOpenMobileMenu={setIsOpenMobileMenu}
                utilityLinks={utilityLinks}
                buttonCTA={buttonCTA}
                closeCTA={closeCTA}
              />
            )}
        </GridColumn>
      </Grid>
    </Box>
  );
});
